import React, { useState, useEffect } from 'react';
import { 
  Archive, Trash2, Clock, AlertTriangle, Save, Play, 
  Shield, Calendar, RefreshCw, CheckCircle, FileWarning
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { adminApi } from '@/lib/api';

const RetentionConfigPage = () => {
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [runningArchive, setRunningArchive] = useState(false);
  const [runningAnonymize, setRunningAnonymize] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dryRunResult, setDryRunResult] = useState(null);

  const [formData, setFormData] = useState({
    retention_years: 5,
    auto_archive_days: 365,
    auto_anonymize_days: 1825,
    delete_attachments_on_archive: false,
    notify_before_deletion_days: 30,
    is_active: false
  });

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getRetentionConfig();
      setConfig(response.data.config);
      setStats(response.data.stats);
      setFormData({
        retention_years: response.data.config.retention_years || 5,
        auto_archive_days: response.data.config.auto_archive_days || 365,
        auto_anonymize_days: response.data.config.auto_anonymize_days || 1825,
        delete_attachments_on_archive: response.data.config.delete_attachments_on_archive || false,
        notify_before_deletion_days: response.data.config.notify_before_deletion_days || 30,
        is_active: response.data.config.is_active || false
      });
    } catch (err) {
      setError('Erro ao carregar configuração de retenção');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      await adminApi.updateRetentionConfig(formData);
      setSuccess('Configuração guardada com sucesso');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao guardar configuração');
    } finally {
      setSaving(false);
    }
  };

  const handleRunArchive = async (dryRun = true) => {
    setRunningArchive(true);
    setError('');
    setDryRunResult(null);
    
    try {
      const response = await adminApi.runArchiveJob(dryRun);
      if (dryRun) {
        setDryRunResult({
          type: 'archive',
          ...response.data
        });
      } else {
        setSuccess(`${response.data.archived_count} denúncias arquivadas com sucesso`);
        fetchConfig(); // Refresh stats
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao executar arquivamento');
    } finally {
      setRunningArchive(false);
    }
  };

  const handleRunAnonymize = async (dryRun = true) => {
    setRunningAnonymize(true);
    setError('');
    setDryRunResult(null);
    
    try {
      const response = await adminApi.runAnonymizeJob(dryRun);
      if (dryRun) {
        setDryRunResult({
          type: 'anonymize',
          ...response.data
        });
      } else {
        setSuccess(`${response.data.anonymized_count} denúncias anonimizadas com sucesso`);
        fetchConfig(); // Refresh stats
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao executar anonimização');
    } finally {
      setRunningAnonymize(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-64 bg-slate-200 rounded" />
        <div className="h-64 bg-slate-100 rounded" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="retention-config-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Archive className="w-6 h-6 text-slate-700" />
            Retenção de Dados
          </h1>
          <p className="text-slate-600 mt-1">
            Configure políticas de arquivamento e anonimização conforme Lei 93/2021
          </p>
        </div>
        <Badge variant={formData.is_active ? "default" : "secondary"} className="text-sm">
          {formData.is_active ? "Ativo" : "Inativo"}
        </Badge>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle className="w-4 h-4 text-green-600" />
          <AlertDescription className="text-green-800">{success}</AlertDescription>
        </Alert>
      )}

      {/* Legal Notice */}
      <Alert className="border-amber-200 bg-amber-50">
        <Shield className="w-4 h-4 text-amber-600" />
        <AlertDescription className="text-amber-800">
          <strong>Lei 93/2021:</strong> Os dados pessoais devem ser conservados pelo período mínimo de 5 anos 
          após a conclusão do tratamento da denúncia, podendo ser prolongado se necessário para processos judiciais.
        </AlertDescription>
      </Alert>

      {/* Stats Cards */}
      {stats && (
        <div className="grid md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-slate-600 mb-1">
                <Archive className="w-4 h-4" />
                <span className="text-xs uppercase">Para Arquivar</span>
              </div>
              <p className="text-2xl font-bold">{stats.reports_to_archive}</p>
              <p className="text-xs text-slate-500">denúncias elegíveis</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-slate-600 mb-1">
                <Trash2 className="w-4 h-4" />
                <span className="text-xs uppercase">Para Anonimizar</span>
              </div>
              <p className="text-2xl font-bold">{stats.reports_to_anonymize}</p>
              <p className="text-xs text-slate-500">denúncias elegíveis</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-slate-600 mb-1">
                <FileWarning className="w-4 h-4" />
                <span className="text-xs uppercase">Total Anexos</span>
              </div>
              <p className="text-2xl font-bold">{stats.total_attachments}</p>
              <p className="text-xs text-slate-500">{stats.total_attachments_size_mb} MB</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-slate-600 mb-1">
                <Clock className="w-4 h-4" />
                <span className="text-xs uppercase">Última Execução</span>
              </div>
              <p className="text-sm font-medium">
                {config?.last_retention_run 
                  ? new Date(config.last_retention_run).toLocaleDateString('pt-PT')
                  : 'Nunca executado'}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Configuration Form */}
      <Card>
        <CardHeader>
          <CardTitle>Configuração de Retenção</CardTitle>
          <CardDescription>
            Defina as políticas automáticas de arquivamento e anonimização de dados
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Activation Toggle */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <Label className="text-base font-medium">Retenção Automática Ativa</Label>
              <p className="text-sm text-slate-500">
                Quando ativo, os processos de arquivamento e anonimização podem ser executados
              </p>
            </div>
            <Switch
              checked={formData.is_active}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
              data-testid="retention-active-switch"
            />
          </div>

          <Separator />

          {/* Retention Period */}
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="retention_years" className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Período de Retenção (anos)
              </Label>
              <Input
                id="retention_years"
                type="number"
                min={5}
                value={formData.retention_years}
                onChange={(e) => setFormData(prev => ({ ...prev, retention_years: parseInt(e.target.value) || 5 }))}
                className="mt-1"
                data-testid="retention-years-input"
              />
              <p className="text-xs text-slate-500 mt-1">Mínimo 5 anos (Lei 93/2021)</p>
            </div>

            <div>
              <Label htmlFor="auto_archive_days" className="flex items-center gap-2">
                <Archive className="w-4 h-4" />
                Arquivar após (dias)
              </Label>
              <Input
                id="auto_archive_days"
                type="number"
                min={30}
                value={formData.auto_archive_days}
                onChange={(e) => setFormData(prev => ({ ...prev, auto_archive_days: parseInt(e.target.value) || 365 }))}
                className="mt-1"
                data-testid="archive-days-input"
              />
              <p className="text-xs text-slate-500 mt-1">Denúncias fechadas há mais de X dias</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="auto_anonymize_days" className="flex items-center gap-2">
                <Trash2 className="w-4 h-4" />
                Anonimizar após (dias)
              </Label>
              <Input
                id="auto_anonymize_days"
                type="number"
                min={1825}
                value={formData.auto_anonymize_days}
                onChange={(e) => setFormData(prev => ({ ...prev, auto_anonymize_days: parseInt(e.target.value) || 1825 }))}
                className="mt-1"
                data-testid="anonymize-days-input"
              />
              <p className="text-xs text-slate-500 mt-1">Remove dados pessoais (mínimo 5 anos = 1825 dias)</p>
            </div>

            <div>
              <Label htmlFor="notify_before_deletion_days" className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Notificar antes (dias)
              </Label>
              <Input
                id="notify_before_deletion_days"
                type="number"
                min={7}
                value={formData.notify_before_deletion_days}
                onChange={(e) => setFormData(prev => ({ ...prev, notify_before_deletion_days: parseInt(e.target.value) || 30 }))}
                className="mt-1"
                data-testid="notify-days-input"
              />
              <p className="text-xs text-slate-500 mt-1">Alerta X dias antes de anonimizar</p>
            </div>
          </div>

          <Separator />

          {/* Delete Attachments Toggle */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div>
              <Label className="text-base font-medium text-red-700">Eliminar Anexos ao Arquivar</Label>
              <p className="text-sm text-slate-500">
                Os ficheiros anexados serão removidos permanentemente quando a denúncia for arquivada
              </p>
            </div>
            <Switch
              checked={formData.delete_attachments_on_archive}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, delete_attachments_on_archive: checked }))}
              data-testid="delete-attachments-switch"
            />
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={saving} data-testid="save-retention-btn">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'A guardar...' : 'Guardar Configuração'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Manual Execution */}
      <Card>
        <CardHeader>
          <CardTitle>Execução Manual</CardTitle>
          <CardDescription>
            Execute os processos de arquivamento e anonimização manualmente
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            {/* Archive */}
            <div className="border rounded-lg p-4">
              <h4 className="font-medium text-slate-900 mb-2 flex items-center gap-2">
                <Archive className="w-4 h-4 text-blue-600" />
                Arquivamento
              </h4>
              <p className="text-sm text-slate-600 mb-4">
                Arquiva denúncias fechadas há mais de {formData.auto_archive_days} dias
              </p>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleRunArchive(true)}
                  disabled={runningArchive || !formData.is_active}
                  data-testid="dry-run-archive-btn"
                >
                  {runningArchive ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : null}
                  Simular
                </Button>
                <Button 
                  size="sm"
                  onClick={() => handleRunArchive(false)}
                  disabled={runningArchive || !formData.is_active}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="run-archive-btn"
                >
                  <Play className="w-4 h-4 mr-1" />
                  Executar
                </Button>
              </div>
            </div>

            {/* Anonymize */}
            <div className="border rounded-lg p-4">
              <h4 className="font-medium text-slate-900 mb-2 flex items-center gap-2">
                <Trash2 className="w-4 h-4 text-red-600" />
                Anonimização
              </h4>
              <p className="text-sm text-slate-600 mb-4">
                Anonimiza denúncias com mais de {formData.auto_anonymize_days} dias
              </p>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleRunAnonymize(true)}
                  disabled={runningAnonymize || !formData.is_active}
                  data-testid="dry-run-anonymize-btn"
                >
                  {runningAnonymize ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : null}
                  Simular
                </Button>
                <Button 
                  size="sm"
                  onClick={() => handleRunAnonymize(false)}
                  disabled={runningAnonymize || !formData.is_active}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="run-anonymize-btn"
                >
                  <Play className="w-4 h-4 mr-1" />
                  Executar
                </Button>
              </div>
            </div>
          </div>

          {/* Dry Run Result */}
          {dryRunResult && (
            <Alert className="border-blue-200 bg-blue-50">
              <AlertDescription>
                <strong>Simulação {dryRunResult.type === 'archive' ? 'Arquivamento' : 'Anonimização'}:</strong>{' '}
                {dryRunResult.type === 'archive' 
                  ? `${dryRunResult.reports_to_archive} denúncias seriam arquivadas`
                  : `${dryRunResult.reports_to_anonymize} denúncias seriam anonimizadas`
                }
                {dryRunResult.reports?.length > 0 && (
                  <ul className="mt-2 text-sm space-y-1">
                    {dryRunResult.reports.slice(0, 5).map((r, i) => (
                      <li key={i} className="text-slate-600">
                        • {r.tracking_code} (criada em {new Date(r.created_at).toLocaleDateString('pt-PT')})
                      </li>
                    ))}
                    {dryRunResult.reports.length > 5 && (
                      <li className="text-slate-500">... e mais {dryRunResult.reports.length - 5}</li>
                    )}
                  </ul>
                )}
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RetentionConfigPage;
